import { jsx as _jsx } from "react/jsx-runtime";
import { LocalizationProvider, DatePicker as MUIDatePicker, } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { debounce, styled, } from "@mui/material";
import dayjs from "dayjs";
import { convertPxToRem } from "../../util-functions";
import { lightGreyColors } from "../TreezThemeProvider";
import { Icon } from "../Icon";
const StyledDatePicker = styled(MUIDatePicker)(({ theme }) => ({
    "&.MuiTextField-root": {
        "& .MuiOutlinedInput-root": Object.assign(Object.assign({ borderRadius: convertPxToRem(19), minHeight: convertPxToRem(52), height: convertPxToRem(52), backgroundColor: theme.palette.grey03.main, color: theme.palette.primaryBlack.main }, theme.typography.largeText), { "&.Mui-focused": {
                outline: `${convertPxToRem(2)} solid ${theme.palette.primaryBlack.main}`,
            }, "&.Mui-disabled": {
                color: theme.palette.disabledText.main,
            }, "&.Mui-error": {
                color: theme.palette.error.main,
                outlineColor: theme.palette.error.main,
            } }),
        "& .MuiFormHelperText-root": Object.assign(Object.assign({ color: theme.palette.secondaryText.main }, theme.typography.smallText), { "&.Mui-disabled": {
                color: theme.palette.tertiaryText.main,
            }, "&.Mui-error": {
                color: theme.palette.error.main,
            } }),
        // textfield
        input: {
            paddingTop: convertPxToRem(10),
            paddingBottom: 0,
        },
        // calendar icon button
        button: {
            padding: convertPxToRem(12),
        },
        // textfield label
        label: {
            color: theme.palette.secondaryText.main,
            "&[data-shrink=false]": Object.assign({ marginTop: convertPxToRem(-2) }, theme.typography.largeText),
            "&[data-shrink=true]": Object.assign({ top: convertPxToRem(14) }, theme.typography.smallText),
            "&.Mui-disabled": {
                color: `${theme.palette.disabledText.main} !important`,
            },
            "&.Mui-error": {
                color: theme.palette.error.main,
            },
        },
        // outlined input--remove outline
        fieldset: {
            border: "unset",
        },
    },
}));
const PopperStyles = (disableHighlightToday, value) => ({
    //  calendar popper container
    ".MuiPaper-root": {
        borderRadius: convertPxToRem(19),
        boxShadow: `0 ${convertPxToRem(4)} ${convertPxToRem(16)} rgba(0, 0, 0, 0.16)`,
    },
    // individual date buttons
    ".MuiButtonBase-root": {
        borderRadius: convertPxToRem(12),
        // current date highlight styles if disableHighlightToday is false & no date is selected
        "&[aria-current='date']": {
            backgroundColor: "unset",
            border: !disableHighlightToday && !value
                ? `${convertPxToRem(1)} solid ${lightGreyColors.grey04.main}`
                : "unset",
        },
        "&:hover": {
            backgroundColor: `${lightGreyColors.grey02.main}`,
        },
    },
    // year selection view
    ".MuiPickersYear-root": {
        // individual year buttons
        button: {
            borderRadius: convertPxToRem(12),
            "&:hover": {
                backgroundColor: `${lightGreyColors.grey02.main}`,
            },
        },
    },
    // rotate SwitchViewIcon when switching between calendar and year views
    // mui does this by default when using their default icons,
    // but we have to implement manually when using our own Icon component
    ".MuiPickersCalendarHeader-root": {
        // switch view button
        button: {
            "&[aria-label='calendar view is open, switch to year view']": {
                transform: `rotate(0deg)`,
            },
            "&[aria-label='year view is open, switch to calendar view']": {
                transform: `rotate(180deg)`,
            },
        },
    },
});
const CalendarIcon = (color) => () => _jsx(Icon, { iconName: "Calendar", color: color });
const ChevronLeftIcon = () => _jsx(Icon, { iconName: "ChevronLeft", fontSize: "large" });
const ChevronRightIcon = () => (_jsx(Icon, { iconName: "ChevronRight", fontSize: "large" }));
const ArrowDropdownIcon = () => (_jsx(Icon, { iconName: "ArrowDropdown", fontSize: "large" }));
export const DatePicker = ({ disabled = false, disableHighlightToday = false, error = false, helperText, inputFormat = "MM/DD/YYYY", label, onChange, onError, required = false, testId, value, }) => {
    const calendarIconColor = disabled
        ? "disabledText"
        : value
            ? "primaryBlack"
            : "secondaryText";
    return (_jsx(LocalizationProvider, Object.assign({ dateAdapter: AdapterDayjs }, { children: _jsx(StyledDatePicker, { slots: {
                openPickerIcon: CalendarIcon(calendarIconColor),
                leftArrowIcon: ChevronLeftIcon,
                rightArrowIcon: ChevronRightIcon,
                switchViewIcon: ArrowDropdownIcon,
            }, disabled: disabled, disableHighlightToday: disableHighlightToday, format: inputFormat, label: label, 
            // TODO: debounce should only run when typing in the field, not when selecting a date from the calendar: ticket #141
            // debounce: execute onChange 500ms after user stops interacting with the field
            onChange: onChange && debounce(onChange, 500), onError: onError, slotProps: {
                textField: Object.assign({ autoComplete: "off", error,
                    helperText,
                    required }, { "data-testid": testId }),
                popper: { sx: PopperStyles(disableHighlightToday, value) },
            }, value: value && dayjs(value) }) })));
};
